import PageLayout from "@/components/PageLayout";
import { Link } from "react-router-dom";

const LAST_UPDATED = "1 January 2026";

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10">
      <h2 className="text-lg font-extrabold text-slate-900 mb-3 pb-2 border-b border-slate-200">{title}</h2>
      <div className="space-y-3 text-sm text-slate-600 leading-relaxed">{children}</div>
    </section>
  );
}

export default function Terms() {
  const seoProps = {
    title: "Terms & Conditions — Sendora Bulk SMS Platform",
    description: "Read the Terms and Conditions governing your use of Sendora's bulk SMS platform. Acceptable use, billing, API access and service availability.",
    canonical: "/terms",
    keywords: "Sendora terms and conditions, SMS platform terms, bulk SMS terms of service Nigeria",
    schema: [{
      "@context": "https://schema.org",
      "@type": "WebPage",
      "url": "https://www.sendoras.com/terms",
      "name": "Terms & Conditions — Sendora",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sendoras.com" },
          { "@type": "ListItem", "position": 2, "name": "Terms & Conditions", "item": "https://www.sendoras.com/terms" }
        ]
      }
    }]
  };
  return (
    <PageLayout title="Terms & Conditions" subtitle={`Last updated: ${LAST_UPDATED}. Please read these terms carefully before using Sendora.`} seo={seoProps}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10 text-sm text-amber-800">
          <strong>Important:</strong> By creating an account or using the Sendora platform, you agree to these Terms & Conditions. If you do not agree, please do not use the platform.
        </div>

        <Section id="definitions" title="1. Definitions">
          <p><strong>"Sendora"</strong> refers to Sendora, a product of Bomdrive Limited.</p>
          <p><strong>"Platform"</strong> means the Sendora web application, APIs, and all associated services.</p>
          <p><strong>"Customer"</strong> means any individual or business that creates an account on the platform.</p>
          <p><strong>"Credits"</strong> means the platform's internal unit of exchange used to pay for SMS messages.</p>
          <p><strong>"Sender ID"</strong> means an alphanumeric name that appears as the message sender on recipients' devices.</p>
        </Section>

        <Section id="account" title="2. Account Registration & Eligibility">
          <p>You must be at least 18 years old and represent a legitimate business to create an account. You are responsible for maintaining the confidentiality of your credentials and for all activity under your account.</p>
          <p>You agree to provide accurate registration information and to update it when it changes. Sendora reserves the right to suspend accounts with false or misleading information.</p>
          <p>One account per business entity is permitted. Creating multiple accounts to circumvent rate limits or pricing is prohibited.</p>
        </Section>

        <Section id="acceptable-use" title="3. Acceptable Use Policy">
          <p>You agree that you will <strong>not</strong> use Sendora to send:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Unsolicited bulk messages (spam) to individuals who have not opted in</li>
            <li>Messages containing fraudulent, misleading, or deceptive content</li>
            <li>Phishing, scam, or advance-fee fraud content</li>
            <li>Messages promoting illegal substances, gambling, or pornography</li>
            <li>Political campaign messages without the recipient's prior consent</li>
            <li>Messages impersonating any individual, company, or government agency</li>
          </ul>
          <p>Sendora complies with the Nigerian Communications Commission (NCC) SMS guidelines. All Sender IDs must be used only for their stated purpose. Misuse of a Sender ID may result in immediate suspension.</p>
          <p>We reserve the right to block, filter, or remove any content that violates these terms without notice.</p>
        </Section>

        <Section id="credits-billing" title="4. Credits, Billing & Refunds">
          <p><strong>Credits are non-refundable</strong> once successfully used to send messages. Unused credits may be refunded at Sendora's discretion within 30 days of purchase if requested via support.</p>
          <p>Subscription fees are non-refundable after the trial period unless the service was unavailable due to Sendora's fault for more than 48 continuous hours.</p>
          <p>Sendora reserves the right to adjust credit pricing with 30 days' notice. Existing purchased credits retain their value.</p>
          <p>All prices are in Nigerian Naira (₦) and exclude applicable taxes. Customers are responsible for remitting any applicable VAT.</p>
        </Section>

        <Section id="sender-ids" title="5. Sender IDs">
          <p>Sender IDs must represent your actual brand or business name. Using a Sender ID that impersonates another entity is strictly prohibited and may result in account termination and potential legal action.</p>
          <p>Sendora submits Sender IDs to Nigerian mobile networks for registration. Approval timelines are 24–72 hours and are controlled by the networks, not Sendora.</p>
          <p>Sendora may revoke a Sender ID at any time if it receives a complaint or legal notice from the impersonated entity.</p>
        </Section>

        <Section id="service-availability" title="6. Service Availability & SLAs">
          <p>Sendora targets 99.9% monthly uptime. Planned maintenance windows are communicated at least 48 hours in advance. Credits will not be deducted for messages that fail due to a confirmed Sendora-side outage.</p>
          <p>Delivery rates depend on network providers outside Sendora's control. We do not guarantee 100% delivery rates as some messages may be filtered by carrier anti-spam systems.</p>
        </Section>

        <Section id="termination" title="7. Account Suspension & Termination">
          <p>Sendora may suspend or terminate your account immediately without notice for: violation of the Acceptable Use Policy, providing false registration information, fraud, attempted abuse of the platform, or non-payment of outstanding fees.</p>
          <p>You may close your account at any time by contacting support. Upon account closure, your data will be retained for the period specified in our <Link to="/privacy" className="text-[#0c1e41] underline font-medium">Privacy Policy</Link> and then securely deleted.</p>
        </Section>

        <Section id="liability" title="8. Limitation of Liability">
          <p>Sendora's total liability to you for any claim arising from use of the platform shall not exceed the total credits value you paid in the 3 months preceding the claim.</p>
          <p>Sendora is not liable for: indirect or consequential damages; lost profits or revenue; damage arising from third-party network failures; or losses resulting from your violation of these terms.</p>
        </Section>

        <Section id="governing-law" title="9. Governing Law">
          <p>These Terms are governed by the laws of the Federal Republic of Nigeria. Any disputes shall be resolved in the Lagos State courts, Nigeria.</p>
        </Section>

        <Section id="contact" title="10. Contact">
          <p>Questions about these terms? Email <a href="mailto:legal@sendora.ng" className="text-[#0c1e41] underline font-medium">legal@sendora.ng</a> or visit our <Link to="/contact" className="text-[#0c1e41] underline font-medium">Contact page</Link>.</p>
        </Section>
      </div>
    </PageLayout>
  );
}
